<template>
  <SidebarDrawer
    v-model="mobile_open"
    :expand_storage_key="storage_key_expand"
    variant="plain"
    data_test="navbar-partner-desktop-project"
    @close="emit('close')"
  >
    <template #client_switcher="{ is_expanded }">
      <SidebarClientSwitcher
        :client="current_client"
        :is_expanded="is_expanded"
        :avatar_format="current_client.attributes.picture_logo_format"
        :has_dropdown="has_multiple_profiles"
        data_test="partner-sidebar-switch-client"
      >
        <DropdownOption
          data-test="partner-sidebar-switch-company"
          @click="openSelectTenantModal"
        >
          <AppIcon
            name="regular-building"
            fixed-width
            size="sm"
          />
          <span>{{ $t('navbar.topnavbar.selectTeam') }}</span>
        </DropdownOption>
      </SidebarClientSwitcher>
    </template>

    <template #nav="{ is_expanded }">
      <SidebarNavItem
        :to="{ name: ROUTES_PARTNERS.CLIENT_DASHBOARD }"
        :label="$t('client.labels.dashboard')"
        icon="regular-house"
        :is_active="route.name?.startsWith(ROUTES_PARTNERS.CLIENT_DASHBOARD)"
        :is_expanded="is_expanded"
        data_test="partner-navbar-dashboard"
        :on_click="() => emit('close')"
      />
      <SidebarNavItem
        :to="{ name: ROUTES_PARTNERS.OVERVIEW_ALL_PROJECTS }"
        :label="$t('navbar.partner.projects')"
        icon="regular-layer-group"
        :is_active="route.name === ROUTES_PARTNERS.OVERVIEW_ALL_PROJECTS"
        :is_expanded="is_expanded"
        data_test="partner-navbar-all-projects"
        :on_click="() => emit('close')"
      />
    </template>

    <template #project_list>
      <div class="select-project-compact shrink-0">
        <SearchInput
          v-model="search_query"
          class="w-full"
          :placeholder="$t('project.labels.searchProject')"
        >
          <InputIcon v-if="search_query">
            <AppButton
              variant="text"
              rounded
              severity="secondary"
              size="small"
              class="-my-1 size-6"
              :aria-label="$t('form.actions.clear')"
              @click="search_query = ''"
            >
              <template #icon>
                <AppIcon
                  name="regular-xmark"
                  size="sm"
                />
              </template>
            </AppButton>
          </InputIcon>
        </SearchInput>
      </div>

      <div class="scrollbar-quiet mt-2 -mr-4 flex min-h-0 flex-1 flex-col gap-1 overflow-x-hidden overflow-y-auto pr-2">
        <SidebarProjectSection
          :title="$t('project.labels.followedProjects')"
          :is_open="toggle_projects_active"
          :on_toggle="() => (toggle_projects_active = !toggle_projects_active)"
          :projects="projects_active_filtered"
        >
          <template #default="{ project }">
            <SidebarProjectItemPartner
              :is_active="active_project_id === project.id"
              :project="project"
              @close="emit('close')"
            />
          </template>
        </SidebarProjectSection>

        <SidebarProjectSection
          v-if="projects_deactivated_filtered.length > 0"
          :title="$t('project.labels.deactivatedProjects')"
          :is_open="toggle_projects_deactivated"
          :on_toggle="() => (toggle_projects_deactivated = !toggle_projects_deactivated)"
          :projects="projects_deactivated_filtered"
        >
          <template #default="{ project }">
            <SidebarProjectItemPartner
              :is_active="active_project_id === project.id"
              :project="project"
              @close="emit('close')"
            />
          </template>
        </SidebarProjectSection>
      </div>
    </template>
  </SidebarDrawer>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { ROUTES_PARTNERS } from '@/app/data/route_constants';
import AppButton from '@/app/shared_components/AppButton.vue';
import SidebarDrawer from '@/app/shared_components/sidebar/SidebarDrawer.vue';
import SidebarNavItem from '@/app/shared_components/sidebar/SidebarNavItem.vue';
import SidebarProjectItemPartner from '@/app/_partners/components/sidebars/SidebarProjectItemPartner.vue';
import SidebarProjectSection from '@/app/shared_components/sidebar/SidebarProjectSection.vue';
import SidebarClientSwitcher from '@/app/shared_components/sidebar/SidebarClientSwitcher.vue';
import DropdownOption from '@/app/shared_components/buttons/DropdownOption.vue';
import SearchInput from '@/app/shared_components/inputs/SearchInput.vue';
import SelectTenantModal from '@/app/shared_components/modals/SelectTenantModal.vue';
import { LOCALSTORAGE_OPTIONS, localStorageKey } from '@/store/lib/local_storage';

const { proxy: app } = getCurrentInstance();

const store = useStore();
const route = useRoute();

const props = defineProps({
  modelValue: { type: Boolean },
});

const emit = defineEmits(['close', 'update:modelValue']);

const mobile_open = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
});

const current_client = computed(() => store.getters.current_client);
const current_profile = computed(() => store.getters.current_profile);
const current_profile_true = computed(() => store.getters.current_profile_true);

const storage_key_expand = computed(() => localStorageKey(current_profile.value, LOCALSTORAGE_OPTIONS.NAV_DRAWER));

const has_multiple_profiles = computed(() => current_profile_true.value.attributes.profiles_count > 1);

const projects = ref([]);
const search_query = ref('');
const toggle_projects_active = ref(true);
const toggle_projects_deactivated = ref(false);

function applySearch(list) {
  const search = search_query.value.trim().toLowerCase();
  if (!search) return list;
  return _.filter(list, (project) => _.includes(project.attributes.name.toLowerCase(), search));
}

const projects_active_filtered = computed(() =>
  applySearch(_.filter(projects.value, (project) => project.attributes.activated)),
);

const projects_deactivated_filtered = computed(() =>
  applySearch(_.filter(projects.value, (project) => !project.attributes.activated)),
);

const active_project_id = computed(() => route.params.project_id || null);

const openSelectTenantModal = () => {
  app.$buefy.modal.open({ component: SelectTenantModal });
};

onMounted(async () => {
  projects.value = await store.dispatch('FETCH_PROJECTS');
});
</script>
